import React, { useMemo, useState } from "react";
import { useAtom } from "jotai";
import { useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import { createItemGroup, updateItemGroup } from "features/Products/api";
import { findGroupsAllChildrenIds } from "features/Products/helpers";
import { activeItemsGroupAtom } from "atoms/catalogAtom";

import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from "components/Modal";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { RegularText } from "components/Text";
import { VerticalSpace } from "components/Spacing";

import { buildGroupOptions } from "./groupOptions";
import styles from "./styles.module.scss";

const LABEL_WIDTH = 130;

/** Создание / свойства группы товаров */
export const GroupModal = ({ isEdit, groups }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();
  const [activeGroup, setActiveGroup] = useAtom(activeItemsGroupAtom);

  const groupOptions = useMemo(() => {
    const all = buildGroupOptions(groups);
    if (!isEdit || !activeGroup) return all;

    // Нельзя выбрать себя или любого потомка (цикл в дереве)
    const excluded = new Set([
      String(activeGroup.itemgroupid),
      ...findGroupsAllChildrenIds(groups, [activeGroup.itemgroupid]).map(
        String
      ),
    ]);
    return all.filter((o) => !excluded.has(o.value));
  }, [groups, isEdit, activeGroup]);

  const [name, setName] = useState(isEdit ? (activeGroup?.name ?? "") : "");
  const [position, setPosition] = useState(
    isEdit ? String(activeGroup?.position ?? 0) : "0"
  );
  const [parentOption, setParentOption] = useState(() => {
    const targetId = isEdit
      ? String(activeGroup?.parentid ?? "0")
      : String(activeGroup?.itemgroupid ?? "0");
    return groupOptions.find((o) => o.value === targetId) || groupOptions[0];
  });

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Наименование обязательно");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const common = {
        name: name.trim(),
        parentid: parentOption?.value ?? "0",
        position: Number(position) || 0,
        avaliable: isEdit ? Number(activeGroup?.avaliable ?? 0) : 0,
        onlywhole: isEdit ? Number(activeGroup?.onlywhole ?? 0) : 0,
      };

      if (isEdit) {
        await updateItemGroup({
          groupid: activeGroup.itemgroupid,
          ...common,
        });
        setActiveGroup({ ...activeGroup, ...common });
      } else {
        await createItemGroup({ ...common, importcode: "" });
      }

      queryClient.invalidateQueries(["item-groups"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell width={440}>
      <ModalHeader
        title={isEdit ? activeGroup?.name : "Новая группа"}
        onClose={closeModal}
      />
      <ModalBody className={styles.groupModalBody}>
        {isEdit && (
          <>
            <Input
              label="ID"
              labelWidth={LABEL_WIDTH}
              value={activeGroup?.itemgroupid ?? ""}
              disabled
              inputWidth={220}
            />
            <VerticalSpace size={12} />
          </>
        )}
        <Select
          label="В группе"
          labelWidth={LABEL_WIDTH}
          options={groupOptions}
          value={parentOption}
          onChange={setParentOption}
          width={220}
        />
        <VerticalSpace size={12} />
        <Input
          label="Наименование"
          labelWidth={LABEL_WIDTH}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          placeholder="Горшечка"
          inputWidth={220}
        />
        <VerticalSpace size={12} />
        <Input
          label="Позиция группы"
          labelWidth={LABEL_WIDTH}
          type="number"
          value={position}
          onChange={(ev) => setPosition(ev.target.value)}
          placeholder="0"
          inputWidth={220}
        />
        {!!error && (
          <>
            <VerticalSpace size={12} />
            <RegularText size={13} color="danger">
              {error}
            </RegularText>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
